/**
 * El moviment MÚLTIPLE d'Existències (RPC `move_stock_batch`, OBR-145): mou diversos lots a destins en UNA transacció (tot o res).
 *
 * El contracte, amb els noms de la funció:
 *   move_stock_batch(p_moves jsonb, p_note text default null, p_client_uuid uuid default null) → uuid[]
 *   element = { article_id, supplier_lot_id, finished_lot_id, from_zone_id, from_section_id, from_location_id,
 *               to_zone_id, to_section_id, to_location_id, quantity }   (quantity en UNITAT BASE de l'article)
 * Torna un id de traspàs per element, en l'ORDRE en què s'han enviat. Màxim 200 elements; el servidor rebutja els duplicats (el mateix lot
 * des del mateix origen dues vegades) i les cadenes (el destí d'un element és l'origen d'un altre del mateix lot): aquí es detecten ABANS
 * d'enviar-ho perquè la pantalla no hagi d'esperar un error de xarxa per dir-ho.
 *
 * `client_uuid` (`p_client_uuid`) fa el reintent IDEMPOTENT: un 504 amb commit i el reintent del BackOffice no mouen dues vegades. S'ha de
 * generar UN cop per intent (no per clic) i reutilitzar-lo als reintents; el mateix uuid amb una altra petició és
 * `move_stock_batch_client_uuid_conflict`.
 *
 * Els errors: el missatge de la funció és `move_stock_batch_item:<índex>:<codi>` quan falla un element (l'índex és el de la llista
 * enviada, des de 0) o un codi sol quan és de tot el conjunt. `parseMoveBatchError` els llegeix i `moveBatchErrorKey` en dona la frase.
 */

import { z } from 'zod';
import { uuid, nonEmptyString, quantity } from './common.js';
import { StockPlace } from './stock.js';

/** El màxim d'elements d'un moviment múltiple (el mateix que la funció SQL i `MOVE_BATCH_MAX` del domini). */
export const MOVE_BATCH_MAX_ITEMS = 200;

/** Un element del moviment, tal com el construeix la pantalla (l'origen i el destí com a llocs). */
export const StockMoveBatchItem = z
  .object({
    article_id: uuid,
    supplier_lot_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    from: StockPlace,
    to: StockPlace,
    // La columna del llibre és numeric(14,3): el que s'arrodoneix a zero seria un moviment buit i permanent.
    quantity: quantity.refine((q) => Math.round(q * 1000) > 0, { message: 'must be positive (3 decimals)' }),
  })
  .strict()
  .refine((m) => !(m.supplier_lot_id !== null && m.finished_lot_id !== null), {
    message: 'a movement belongs to one lot at most',
    path: ['finished_lot_id'],
  })
  .refine((m) => m.to.zone_id !== null, {
    message: 'the destination needs at least a zone',
    path: ['to', 'zone_id'],
  })
  .refine(
    (m) =>
      m.from.zone_id !== m.to.zone_id ||
      m.from.section_id !== m.to.section_id ||
      m.from.location_id !== m.to.location_id,
    { message: 'origin and destination are the same place', path: ['to'] },
  );
export type StockMoveBatchItem = z.infer<typeof StockMoveBatchItem>;

/** Un conflicte entre dos elements del mateix conjunt (el servidor els rebutja amb el mateix codi). */
export interface MoveBatchConflict {
  /** L'índex de l'element que es rebutja (el segon d'una parella). */
  readonly index: number;
  readonly code: 'duplicate' | 'chained';
}

function samePlace(a: z.infer<typeof StockPlace>, b: z.infer<typeof StockPlace>): boolean {
  return a.zone_id === b.zone_id && a.section_id === b.section_id && a.location_id === b.location_id;
}

/**
 * Cerca els elements que el servidor rebutjaria pel conjunt (no per cap element sol): el mateix lot (o el mateix article sense lot) des del
 * mateix origen dues vegades, i les cadenes (el destí d'un és l'origen d'un altre del mateix lot). Compara els llocs tal com vénen, que a la
 * pantalla ja són els normalitzats de `stock_balances`. Torna el primer, o null.
 */
export function findMoveBatchConflict(moves: readonly StockMoveBatchItem[]): MoveBatchConflict | null {
  const sameLot = (a: StockMoveBatchItem, b: StockMoveBatchItem): boolean =>
    a.article_id === b.article_id &&
    a.supplier_lot_id === b.supplier_lot_id &&
    a.finished_lot_id === b.finished_lot_id;
  for (let j = 0; j < moves.length; j++) {
    for (let i = 0; i < j; i++) {
      const a = moves[i] as StockMoveBatchItem;
      const b = moves[j] as StockMoveBatchItem;
      if (sameLot(a, b) && samePlace(a.from, b.from)) return { index: j, code: 'duplicate' };
    }
  }
  for (let j = 0; j < moves.length; j++) {
    for (let i = 0; i < moves.length; i++) {
      if (i === j) continue;
      const a = moves[i] as StockMoveBatchItem;
      const b = moves[j] as StockMoveBatchItem;
      if (sameLot(a, b) && samePlace(a.to, b.from)) return { index: j, code: 'chained' };
    }
  }
  return null;
}

/** El moviment múltiple sencer: els elements, la nota (opcional) i l'identificador d'idempotència (opcional). */
export const StockMoveBatchInput = z
  .object({
    moves: z.array(StockMoveBatchItem).min(1).max(MOVE_BATCH_MAX_ITEMS),
    note: nonEmptyString.optional(),
    client_uuid: uuid.nullable().optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    const conflict = findMoveBatchConflict(input.moves);
    if (conflict !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `move_stock_batch_${conflict.code}`,
        path: ['moves', conflict.index],
      });
    }
  });
export type StockMoveBatchInput = z.infer<typeof StockMoveBatchInput>;

/**
 * Els arguments de l'RPC `move_stock_batch`, amb els noms exactes de la funció (`p_…`). Cada element porta les deu claus (les nul·les amb
 * valor null: una clau que falta la rebutja el servidor, perquè no es converteixi en silenci en «sense lloc»); la quantitat va arrodonida a
 * tres decimals, com la columna.
 */
export function moveBatchRpcArgs(input: StockMoveBatchInput): Record<string, unknown> {
  return {
    p_moves: input.moves.map((m) => ({
      article_id: m.article_id,
      supplier_lot_id: m.supplier_lot_id,
      finished_lot_id: m.finished_lot_id,
      from_zone_id: m.from.zone_id,
      from_section_id: m.from.section_id,
      from_location_id: m.from.location_id,
      to_zone_id: m.to.zone_id,
      to_section_id: m.to.section_id,
      to_location_id: m.to.location_id,
      quantity: Math.round(m.quantity * 1000) / 1000,
    })),
    p_note: input.note ?? null,
    p_client_uuid: input.client_uuid ?? null,
  };
}

/**
 * Els elements d'un pla de moviment (`planBatchMove` del domini) cap a un destí comú. Només el que es mou (no el que ja és al destí),
 * en l'ordre del pla. Estructural: no depèn del domini, perquè aquest fitxer sigui només el contracte de l'RPC.
 */
export function batchItemsFromPlan(
  movable: ReadonlyArray<{
    readonly quantity: number | null;
    readonly candidate: {
      readonly articleId: string;
      readonly supplierLotId: string | null;
      readonly finishedLotId: string | null;
      readonly place: z.infer<typeof StockPlace>;
    };
  }>,
  destination: z.infer<typeof StockPlace>,
): StockMoveBatchItem[] {
  return movable.map((line) => ({
    article_id: line.candidate.articleId,
    supplier_lot_id: line.candidate.supplierLotId,
    finished_lot_id: line.candidate.finishedLotId,
    from: line.candidate.place,
    to: destination,
    // `null` no hauria d'arribar aquí (un pla vàlid no en porta); si hi arriba, el parse de l'input el rebutja amb un error clar.
    quantity: line.quantity ?? 0,
  }));
}

// ── Errors ─────────────────────────────────────────────────────────────────────────────

/** Els errors de tot el conjunt (no d'un element). */
export const MOVE_BATCH_SET_ERROR_CODES = [
  'move_stock_batch_empty',
  'move_stock_batch_too_large',
  'move_stock_batch_client_uuid_conflict',
  'move_stock_forbidden',
  'move_stock_no_tenant',
] as const;

/** Els motius pels quals pot fallar UN element: els de `move_stock` i els del conjunt (forma, duplicat, cadena). */
export const MOVE_BATCH_ITEM_ERROR_CODES = [
  'move_stock_batch_bad_item',
  'move_stock_batch_duplicate',
  'move_stock_batch_chained',
  'move_stock_insufficient',
  'move_stock_same_place',
  'move_stock_no_destination',
  'move_stock_bad_destination',
  'move_stock_destination_without_stock',
  'move_stock_two_lots',
  'move_stock_unknown_article',
  'move_stock_unknown_lot',
  'move_stock_bad_quantity',
] as const;

export type MoveBatchSetErrorCode = (typeof MOVE_BATCH_SET_ERROR_CODES)[number];
export type MoveBatchItemErrorCode = (typeof MOVE_BATCH_ITEM_ERROR_CODES)[number];
export type MoveBatchErrorCode = MoveBatchSetErrorCode | MoveBatchItemErrorCode;

export interface MoveBatchError {
  /** El motiu, o `unknown` si no és cap dels coneguts (la pantalla en diu una frase genèrica). */
  readonly code: MoveBatchErrorCode | 'unknown';
  /** L'índex de l'element que ha fallat (des de 0, de la llista enviada), o null si l'error és de tot el conjunt. */
  readonly index: number | null;
}

/**
 * Llegeix l'error d'`move_stock_batch` del missatge que torna el servidor. `move_stock_batch_item:<índex>:<codi>` és d'un element; un codi sol
 * és de tot el conjunt. Un missatge que no s'entén és `unknown` sense índex (la pantalla diu «no s'ha mogut res», que sempre és cert: és
 * tot o res).
 */
export function parseMoveBatchError(message: string | null | undefined): MoveBatchError {
  if (!message) return { code: 'unknown', index: null };
  const item = /move_stock_batch_item:(\d+):([a-z0-9_]+)/.exec(message);
  if (item) {
    const code = (MOVE_BATCH_ITEM_ERROR_CODES as readonly string[]).includes(item[2] as string)
      ? (item[2] as MoveBatchItemErrorCode)
      : 'unknown';
    return { code, index: Number(item[1]) };
  }
  const set = MOVE_BATCH_SET_ERROR_CODES.find((code) => message.includes(code));
  return { code: set ?? 'unknown', index: null };
}

/** La clau i18n (`backoffice.stockViews:err_<codi>`) de la frase d'un error del moviment múltiple. */
export function moveBatchErrorKey(error: MoveBatchError): string {
  return `backoffice.stockViews:err_${error.code}`;
}

/** Tots els codis que tenen frase (la prova d'i18n comprova que hi són a les tres llengües). */
export const MOVE_BATCH_ERROR_CODES: readonly (MoveBatchErrorCode | 'unknown')[] = [
  ...MOVE_BATCH_SET_ERROR_CODES,
  ...MOVE_BATCH_ITEM_ERROR_CODES,
  'unknown',
];
